import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Crown, Calculator, Store, ShoppingBag, Eye, EyeOff } from "lucide-react";
import { useAuth, API } from "../context/AuthContext";
import { SetPasswordModal } from "../components/shared/SetPasswordModal";
import { useTenant } from "../context/TenantContext";

const ROLES = [
  { id: "finance_admin", label: "Finance Admin", icon: Crown, color: "bg-[#8b5cf6]", endpoint: "/finance-admin/login" },
  { id: "accountant", label: "Accountant", icon: Calculator, color: "bg-[#f5a623]", endpoint: "/accountant/login" },
  { id: "merchant", label: "Merchant", icon: Store, color: "bg-[#2d8a4e]", endpoint: "/merchant/login" },
  { id: "shop_admin", label: "Shop Admin", icon: ShoppingBag, color: "bg-[#0ea5e9]", endpoint: "/shop-admin/login" },
];

export default function UnifiedAdminLogin() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { login } = useAuth();
  const { tenant } = useTenant();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRole) return toast.error("Please select a role");
    if (!email || !password) return toast.error("Enter email and password");
    setLoading(true);
    try {
      const res = await axios.post(`${API}${selectedRole.endpoint}`, { email, password });
      if (res.data.is_first_login) {
        login(res.data.token, { role: selectedRole.id, name: res.data.name, is_first_login: true });
        setShowSetPassword(true);
      } else {
        login(res.data.token, { role: selectedRole.id, name: res.data.name });
        toast.success("Login successful");
        navigate(`/${tenantSlug}/admin/dashboard`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  const handleSetPassword = async (newPassword) => {
    try {
      // Cookie-based session — the browser attaches the httpOnly access_token
      // cookie automatically. We don't need to read the token from
      // localStorage anymore for this call.
      const endpoint = selectedRole.id === "admin" ? "/admin/set-password"
        : selectedRole.id === "finance_admin" ? "/finance-admin/set-password"
        : selectedRole.id === "accountant" ? "/accountant/set-password"
        : selectedRole.id === "shop_admin" ? "/shop-admin/set-password"
        : "/merchant/set-password";
      await axios.post(`${API}${endpoint}`, { new_password: newPassword }, { withCredentials: true });
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      login(localStorage.getItem("token") || "", { ...userData, is_first_login: false });
      toast.success("Password set successfully");
      navigate(`/${tenantSlug}/admin/dashboard`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error setting password");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-[#f8fafc] to-[#e2e8f0]" data-testid="admin-login">
      <button onClick={() => navigate("/")} className="absolute top-6 left-6 p-2 rounded-full hover:bg-white/50">
        <ArrowLeft className="w-6 h-6 text-slate-600" />
      </button>

      <img src={tenant?.logo_url || "/mymaitree-logo.png"} alt={tenant?.name || "Admin"} className="h-12 w-auto mx-auto mb-4" />
      <h1 className="text-2xl font-extrabold text-[#1e3a5f] mb-1">{tenant?.name || "Admin Portal"}</h1>
      <p className="text-slate-500 text-sm mb-6">Select your role and log in</p>

      {/* Role selector — 2 cols on phones (labels have room), 4 on tablets+.
          Container widens on md+ so labels don't wrap awkwardly on desktop. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-md md:max-w-2xl mb-8">
        {ROLES.map(role => (
          <button
            key={role.id}
            onClick={() => setSelectedRole(role)}
            className={`flex flex-col items-center p-3 sm:p-5 rounded-2xl border-2 transition-all ${selectedRole?.id === role.id ? `${role.color} text-white border-transparent shadow-lg scale-105` : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:shadow-md"}`}
            data-testid={`role-${role.id}`}
          >
            <role.icon className="w-7 h-7 mb-2" />
            <span className="text-xs sm:text-sm font-medium leading-tight text-center whitespace-nowrap md:whitespace-normal break-words w-full">{role.label}</span>
          </button>
        ))}
      </div>

      {/* Login form */}
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-3">
        <input
          type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="Email" className="w-full p-4 rounded-2xl border bg-white"
          data-testid="admin-email"
        />
        <div className="relative">
          <input
            type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Password" className="w-full p-4 rounded-2xl border bg-white pr-12"
            data-testid="admin-password"
          />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
            {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <button
          type="submit" disabled={loading || !selectedRole}
          className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50"
          data-testid="login-btn"
        >
          {loading ? "Logging in..." : selectedRole ? `Login as ${selectedRole.label}` : "Select a role"}
        </button>
      </form>

      {showSetPassword && <SetPasswordModal title="Set New Password" onSubmit={handleSetPassword} />}
    </div>
  );
}
